import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { importPrivateKey, unwrapOMK } from '../utils/cryptoUtils';
import { managementService } from '../services/management/managementService';

/* ------------------------------------------------------------------ */
/*  IndexedDB helpers — store the PEM string, not the CryptoKey        */
/*  (CryptoKey with extractable:false can't be serialized to IDB)      */
/* ------------------------------------------------------------------ */
const IDB_NAME = 'saf3ai-keys';
const IDB_STORE = 'decryption';
const IDB_KEY = 'rsa-private-pem';

function openKeysDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function saveKeyToIDB(pemString) {
  const db = await openKeysDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(pemString, IDB_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function loadKeyFromIDB() {
  const db = await openKeysDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const req = tx.objectStore(IDB_STORE).get(IDB_KEY);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

async function deleteKeyFromIDB() {
  const db = await openKeysDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).delete(IDB_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/* ------------------------------------------------------------------ */
/*  Context                                                            */
/* ------------------------------------------------------------------ */
const DecryptionKeyContext = createContext({
  rsaPrivateKey: null,
  isKeyLoaded: false,
  isKeyPersisted: false,
  keyError: null,
  omk: null,
  isOmkReady: false,
  loadKeyFromPem: async () => {},
  loadKeyFromFile: async () => {},
  persistKey: async () => {},
  clearKey: () => {},
});

export function DecryptionKeyProvider({ children }) {
  const [rsaPrivateKey, setRsaPrivateKey] = useState(null);
  const [keyError, setKeyError] = useState(null);
  const [isKeyPersisted, setIsKeyPersisted] = useState(false);
  const [rawPem, setRawPem] = useState(null);

  // OMK state — unwrapped OMK as ArrayBuffer, ready for DEK unwrapping
  const [omk, setOmk] = useState(null);
  const [isOmkReady, setIsOmkReady] = useState(false);
  const omkFetchRef = useRef(0);

  // Auto-load persisted key from IndexedDB on mount
  useEffect(() => {
    (async () => {
      try {
        const pem = await loadKeyFromIDB();
        if (pem) {
          const cryptoKey = await importPrivateKey(pem);
          setRsaPrivateKey(cryptoKey);
          setRawPem(pem);
          setIsKeyPersisted(true);
        }
      } catch {
        // Silently ignore — key might be corrupt or IDB unavailable
      }
    })();
  }, []);

  // When RSA key is loaded, fetch rsa_wrapped_omk from API and unwrap it
  useEffect(() => {
    if (!rsaPrivateKey) {
      setOmk(null);
      setIsOmkReady(false);
      return;
    }

    const fetchId = ++omkFetchRef.current;

    (async () => {
      try {
        const resp = await managementService.getDataPolicy();
        const policy = resp.data;

        if (fetchId !== omkFetchRef.current) return;

        if (!policy?.rsa_wrapped_omk) {
          // No OMK set — org may not be in encrypted mode
          setOmk(null);
          setIsOmkReady(false);
          return;
        }

        const unwrappedOmk = await unwrapOMK(policy.rsa_wrapped_omk, rsaPrivateKey);
        if (fetchId !== omkFetchRef.current) return;

        setOmk(unwrappedOmk);
        setIsOmkReady(true);
      } catch (err) {
        if (fetchId !== omkFetchRef.current) return;
        console.warn('Failed to unwrap OMK:', err.message);
        setOmk(null);
        setIsOmkReady(false);
      }
    })();
  }, [rsaPrivateKey]);

  const loadKeyFromPem = useCallback(async (pemString, { persist = false } = {}) => {
    try {
      setKeyError(null);
      const cryptoKey = await importPrivateKey(pemString);
      setRsaPrivateKey(cryptoKey);
      setRawPem(pemString);

      if (persist) {
        await saveKeyToIDB(pemString);
        setIsKeyPersisted(true);
      } else {
        setIsKeyPersisted(false);
      }
    } catch (err) {
      setKeyError(`Invalid private key: ${err.message}`);
      setRsaPrivateKey(null);
      setRawPem(null);
      throw err;
    }
  }, []);

  const loadKeyFromFile = useCallback(async (file, { persist = false } = {}) => {
    const text = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
    await loadKeyFromPem(text, { persist });
  }, [loadKeyFromPem]);

  // Save current in-memory key to IndexedDB (called when user checks "remember")
  const persistKey = useCallback(async () => {
    if (rawPem) {
      await saveKeyToIDB(rawPem);
      setIsKeyPersisted(true);
    }
  }, [rawPem]);

  const clearKey = useCallback(async () => {
    setRsaPrivateKey(null);
    setRawPem(null);
    setKeyError(null);
    setIsKeyPersisted(false);
    setOmk(null);
    setIsOmkReady(false);
    try {
      await deleteKeyFromIDB();
    } catch {
      // ignore
    }
  }, []);

  return (
    <DecryptionKeyContext.Provider
      value={{
        rsaPrivateKey,
        isKeyLoaded: rsaPrivateKey !== null,
        isKeyPersisted,
        keyError,
        omk,
        isOmkReady,
        loadKeyFromPem,
        loadKeyFromFile,
        persistKey,
        clearKey,
      }}
    >
      {children}
    </DecryptionKeyContext.Provider>
  );
}

export function useDecryptionKey() {
  return useContext(DecryptionKeyContext);
}

export default DecryptionKeyContext;
